import { formatUrl } from "util";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { ClearButton, Typeahead as BSTypeahead } from "react-bootstrap-typeahead";
import { useDispatch, useSelector } from "react-redux";

import { fetchCommandersTypeahead, fetchPartnersTypeahead, fetchSetsTypeahead, fetchTypeahead } from "store/typeahead";

const filterBy = (option, props) => {
  const helper = (option) => formatUrl(option).startsWith(formatUrl(props.text));
  return typeof option === "string" ? helper(option) : Object.values(option).some((value) => helper(value));
};

const Typeahead = ({
  className,
  clearButton,
  clearOnChange,
  icon: inIcon,
  onChange,
  placeholder,
  selected,
  showLoading,
  type,
}) => {
  const dispatch = useDispatch();
  const ref = useRef();
  const commandersTypeahead = useSelector((state) => state.typeahead.commandersTypeahead);
  const isLoadingCommandersTypeahead = useSelector((state) => state.typeahead.isLoadingCommandersTypeahead);
  const isLoadingPartnersTypeahead = useSelector((state) => state.typeahead.isLoadingPartnersTypeahead);
  const isLoadingSetsTypeahead = useSelector((state) => state.typeahead.isLoadingSetsTypeahead);
  const isLoadingDefaultTypeahead = useSelector((state) => state.typeahead.isLoadingTypeahead);
  const partnersTypeahead = useSelector((state) => state.typeahead.partnersTypeahead);
  const setsTypeahead = useSelector((state) => state.typeahead.setsTypeahead);
  const typeahead = useSelector((state) => state.typeahead.typeahead);
  const isLoading = useSelector((state) => state.pagedata.isLoading);
  const [icon, setIcon] = useState(inIcon || "search");

  useEffect(() => {
    if (type === "commanders" && commandersTypeahead.length === 0 && !isLoadingCommandersTypeahead) {
      dispatch(fetchCommandersTypeahead());
    } else if (type === "partners" && partnersTypeahead.length === 0 && !isLoadingPartnersTypeahead) {
      dispatch(fetchPartnersTypeahead());
    } else if (type === "sets" && setsTypeahead.length === 0 && !isLoadingSetsTypeahead) {
      dispatch(fetchSetsTypeahead());
    } else if (typeahead.length === 0 && !isLoadingDefaultTypeahead) {
      dispatch(fetchTypeahead());
    }
  }, [type]);

  const isLoadingTypeahead =
    type === "commanders"
      ? isLoadingCommandersTypeahead
      : type === "partners"
      ? isLoadingPartnersTypeahead
      : isLoadingSetsTypeahead
      ? type === "sets"
      : type === "tags"
      ? false
      : isLoadingDefaultTypeahead;

  useEffect(() => {
    if (isLoadingTypeahead || (showLoading && isLoading)) {
      setIcon("spinner");
    } else {
      setIcon(inIcon || "search");
    }
  }, [isLoading, isLoadingTypeahead]);

  const options =
    type === "commanders"
      ? commandersTypeahead
      : type === "partners"
      ? partnersTypeahead
      : type === "sets"
      ? setsTypeahead
      : type === "tags"
      ? [
          { id: "draw", name: "Card Draw" },
          { id: "protection", name: "Protection" },
          { id: "ramp", name: "Ramp" },
          { id: "recursion", name: "Recursion" },
          { id: "removal", name: "Removal" },
          { id: "sweeper", name: "Board Wipe" },
          { id: "tutor", name: "Tutors" },
        ]
      : typeahead;

  return (
    <BSTypeahead
      inputProps={{
        autoCapitalize: "off",
        autoCorrect: "off",
        spellCheck: false,
      }}
      className={className}
      disabled={isLoadingTypeahead}
      filterBy={filterBy}
      id="searchbar"
      labelKey="name"
      minLength={["sets", "tags"].includes(type) ? 0 : 3}
      multiple={["sets", "tags"].includes(type)}
      onChange={(e) => {
        if (e.length > 0 && e[0].length > 0) {
          ref.current.blur();
        }
        if (clearOnChange) {
          ref.current.clear();
        }
        onChange(e);
      }}
      options={options}
      placeholder={placeholder}
      ref={ref}
      selected={selected}
    >
      {({ onClear, selected }) =>
        clearButton ? (
          <div className="rbt-aux">
            <ClearButton className="color-black" onClick={onClear} />
          </div>
        ) : (
          <div className="rbt-aux">
            <FontAwesomeIcon
              className={classNames("color-black", {
                "fa-spin": icon === "spinner",
              })}
              icon={icon}
            />
          </div>
        )
      }
    </BSTypeahead>
  );
};

Typeahead.propTypes = {
  className: PropTypes.string,
  clearButton: PropTypes.bool,
  clearOnChange: PropTypes.bool,
  icon: PropTypes.node,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  selected: PropTypes.arrayOf(PropTypes.string.isRequired),
  showLoading: PropTypes.bool,
  spinner: PropTypes.bool,
  type: PropTypes.string,
};

export default Typeahead;
